import axios from 'axios'
import * as Constants from '../constants'
import { TokenT } from '../types/tokenT'
import { store } from '../app/store'
import { setIsLoggedIn } from '../pages/login/slice'

const PROACTIVE_REFRESH_BUFFER = 5 * 60 * 1000

export default class Token {
  private static refreshPromise: Promise<TokenT | null> | null = null

  static clearStoredToken(): void {
    localStorage.removeItem(Constants.GTOB_TOKEN_IDENTIFIER)
    localStorage.removeItem(Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER)
    localStorage.removeItem(Constants.GTOB_TOKEN_LONG_EXPIRATION_IDENTIFIER)
  }

  static clearAndLogout(): void {
    Token.clearStoredToken()
    store.dispatch(setIsLoggedIn(false))
  }

  static tokenIncludesRequiredScopes(token: TokenT): boolean {
    const granted = token.scope?.split(/ +/).filter(Boolean) ?? []
    return Constants.LOGIN_SCOPES_ARRAY.every((scope) => granted.includes(scope))
  }

  static hasRequiredScopes(): boolean {
    const token = Token.getStoredToken()
    if (!token) {
      return false
    }
    return Token.tokenIncludesRequiredScopes(token)
  }

  static getStoredToken(): TokenT | null {
    const stringToken = localStorage.getItem(Constants.GTOB_TOKEN_IDENTIFIER)
    if (!stringToken) {
      return null
    }
    try {
      return JSON.parse(stringToken) as TokenT
    } catch {
      return null
    }
  }

  static persistToken(token: TokenT): void {
    localStorage.setItem(Constants.GTOB_TOKEN_IDENTIFIER, JSON.stringify(token))
    localStorage.setItem(
      Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER,
      (new Date().getTime() + token.expires_in * 1000).toString()
    )
    localStorage.setItem(
      Constants.GTOB_TOKEN_LONG_EXPIRATION_IDENTIFIER,
      (new Date().getTime() + Constants.LONG_EXPIRATION_TIME * 1000).toString()
    )
  }

  static async getValue(): Promise<string | null> {
    const token = Token.getStoredToken()
    if (!token) {
      return null
    }

    if (!Token.hasRequiredScopes()) {
      Token.clearAndLogout()
      return null
    }

    if (Token.isValid(Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER)) {
      return token.access_token
    }

    const newToken = await Token.refresh(token, true)
    return newToken ? newToken.access_token : null
  }

  static isValid(expirationDateIdentifier: string | undefined): boolean {
    const expirationDate = localStorage.getItem(expirationDateIdentifier ?? '')
    return expirationDate ? Number(expirationDate) > new Date().getTime() : false
  }

  static isExpiringSoon(): boolean {
    const expirationDate = localStorage.getItem(Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER)
    if (!expirationDate) return true
    return Number(expirationDate) - new Date().getTime() < PROACTIVE_REFRESH_BUFFER
  }

  static async ensureValidToken(): Promise<boolean> {
    const token = Token.getStoredToken()
    if (!token) return false

    if (!Token.hasRequiredScopes()) {
      Token.clearAndLogout()
      return false
    }

    if (Token.isValid(Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER) && !Token.isExpiringSoon()) {
      return true
    }

    if (!Token.isValid(Constants.GTOB_TOKEN_LONG_EXPIRATION_IDENTIFIER)) {
      return false
    }

    const newToken = await Token.refresh(token, false)
    return newToken !== null
  }

  static async silentRefresh(): Promise<string | null> {
    const token = Token.getStoredToken()
    if (!token) return null

    if (!Token.hasRequiredScopes()) {
      return null
    }

    const newToken = await Token.refresh(token, false)
    return newToken ? newToken.access_token : null
  }

  private static async refresh(token: TokenT, dispatchLogoutOnFailure: boolean) {
    if (Token.refreshPromise) {
      return Token.refreshPromise
    }

    Token.refreshPromise = Token.performRefresh(token, dispatchLogoutOnFailure)

    try {
      return await Token.refreshPromise
    } finally {
      Token.refreshPromise = null
    }
  }

  private static async performRefresh(
    token: TokenT,
    dispatchLogoutOnFailure: boolean
  ): Promise<TokenT | null> {
    try {
      const requestData = new URLSearchParams()
      requestData.append('grant_type', 'refresh_token')
      requestData.append('client_id', process.env.REACT_APP_OAUTH_CLIENT_ID ?? '')
      requestData.append('refresh_token', token.refresh_token)
      const newToken = await axios<TokenT>({
        url: `${process.env.REACT_APP_API_URL ?? ''}/oauth/token`,
        method: 'post',
        data: requestData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
      })

      if (!Token.tokenIncludesRequiredScopes(newToken.data)) {
        if (dispatchLogoutOnFailure) {
          Token.clearAndLogout()
        }
        return null
      }

      Token.persistToken(newToken.data)
      return newToken.data
    } catch {
      if (dispatchLogoutOnFailure) {
        Token.clearAndLogout()
      }
      return null
    }
  }
}
